import { computed, reactive, ref } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { useAppToast } from "@/shared/composables/useAppToast";
import { getConsentMatrix, updateConsentMatrix, } from "../infrastructure/adapters/consentMatrixService";
import { listConsentPurposes } from "../infrastructure/adapters/consentPurposeService";
export function useConsentMatrix() {
    const matrix = reactive({ updatedAt: "", entries: [] });
    const purposeCatalogue = ref([]);
    let originalSnapshot = "";
    const isLoading = ref(false);
    const isSaving = ref(false);
    const loadError = ref(false);
    const { toastSuccess, toastError } = useAppToast();
    const isDirty = computed(() => JSON.stringify(matrix) !== originalSnapshot);
    const channels = computed(() => [...new Set(matrix.entries.map((e) => e.channelType))].sort());
    const purposes = computed(() => [...new Set(matrix.entries.map((e) => e.purpose))].sort());
    const diff = computed(() => {
        // Iterate matrix.entries FIRST so Vue tracks the reactive dep on every
        // computed invocation. An early return on !originalSnapshot would skip
        // the read and Vue would cache the empty array forever — subsequent
        // toggles wouldn't invalidate the computed.
        const out = [];
        const currentEntries = matrix.entries;
        if (!originalSnapshot) {
            return out;
        }
        const original = JSON.parse(originalSnapshot);
        const byKey = new Map(original.entries.map((e) => [`${e.channelType}|${e.purpose}`, e.isEnabled]));
        for (const e of currentEntries) {
            const before = byKey.get(`${e.channelType}|${e.purpose}`);
            if (before !== undefined && before !== e.isEnabled) {
                out.push({ channelType: e.channelType, purpose: e.purpose, before, after: e.isEnabled });
            }
        }
        return out;
    });
    function snapshot(next) {
        Object.assign(matrix, next);
        originalSnapshot = JSON.stringify(matrix);
    }
    async function load() {
        isLoading.value = true;
        loadError.value = false;
        try {
            const [matrixData, purposes] = await Promise.all([
                getConsentMatrix(),
                listConsentPurposes(),
            ]);
            snapshot(matrixData);
            purposeCatalogue.value = purposes;
        }
        catch (err) {
            logger.error("Failed to load consent matrix", err);
            loadError.value = true;
        }
        finally {
            isLoading.value = false;
        }
    }
    function toggle(channel, purpose) {
        const entry = matrix.entries.find((e) => e.channelType === channel && e.purpose === purpose);
        if (!entry) {
            return;
        }
        entry.isEnabled = !entry.isEnabled;
    }
    async function save() {
        isSaving.value = true;
        try {
            const next = await updateConsentMatrix({ entries: matrix.entries });
            snapshot(next);
            toastSuccess("consent.matrix.saveSuccess");
        }
        catch (err) {
            logger.error("Failed to save consent matrix", err);
            toastError("consent.matrix.saveError");
        }
        finally {
            isSaving.value = false;
        }
    }
    function revert() {
        if (originalSnapshot) {
            Object.assign(matrix, JSON.parse(originalSnapshot));
        }
    }
    return { matrix, channels, purposes, purposeCatalogue, isLoading, isSaving, loadError, isDirty, diff, load, toggle, save, revert };
}
